import { useState, useCallback } from "react";

interface NmiScriptConfig {
  apiKey: string;
  scriptId: string;
  scriptUrl: string;
}

export const useNmiScriptLoader = () => {
  const [nmiScriptLoaded, setNmiScriptLoaded] = useState(false);
  const [currentApiKey, setCurrentApiKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const removeExistingScript = useCallback((scriptId: string) => {
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }
  }, []);

  const loadNmiScript = useCallback(
    (config: NmiScriptConfig) => {
      const { apiKey, scriptId, scriptUrl } = config;

      if (nmiScriptLoaded && currentApiKey === apiKey) {
        return;
      }

      setIsLoading(true);

      try {
        removeExistingScript(scriptId);

        const script = document.createElement("script");
        script.id = scriptId;
        script.src = scriptUrl;
        script.setAttribute("data-tokenization-key", apiKey);
        script.setAttribute("data-field-ccnumber-enable-card-brand-previews", "true");
        script.setAttribute("data-style-sniffer", "true");

        script.onerror = () => {
          console.error("Failed to load NMI script");
          setIsLoading(false);
        };

        script.onload = () => {
          setNmiScriptLoaded(true);
          setCurrentApiKey(apiKey);
          setIsLoading(false);
        };

        document.head.appendChild(script);
      } catch (error) {
        console.error("Error loading NMI script");
        setIsLoading(false);
      }
    },
    [nmiScriptLoaded, currentApiKey, removeExistingScript]
  );

  const unloadNmiScript = useCallback(
    (scriptId: string) => {
      removeExistingScript(scriptId);
      setNmiScriptLoaded(false);
      setCurrentApiKey(null);
    },
    [removeExistingScript]
  );

  return {
    loadNmiScript,
    unloadNmiScript,
    nmiScriptLoaded,
    currentApiKey,
    isLoading,
  };
};
